import { useEffect, useEffectEvent, useState } from 'react';

// scrollIntoView's smooth-scroll animation settles the target's top within
// a fraction of a pixel of the container's top, not always exactly 0 (e.g.
// browser rounding, subpixel layout). A strict `<= 0` comparison can then
// miss a heading that has, for all practical purposes, reached the top.
const TOP_EPSILON_PX = 2;

// A section is active from the moment its header reaches the top of the
// container until the next header reaches the top. Recomputed from real
// element positions on every scroll tick (rather than an
// IntersectionObserver, which only reports a heading's crossing at the
// instant it happens, not "has this header reached the top right now"),
// so it can't get stuck on stale state or drift out of sync when
// scrolling back up.
//
// If a section can't be scrolled all the way flush to the top (e.g. not
// enough content below it), it simply never becomes active — that's
// expected, not a bug to work around.
const getActiveSectionId = (sectionIds: string[], container: HTMLElement): string | null => {
  const containerTop = container.getBoundingClientRect().top;
  let activeId = sectionIds[0] ?? null;

  for (const id of sectionIds) {
    const element = document.getElementById(id);
    if (!element) continue;

    const hasReachedTop = element.getBoundingClientRect().top - containerTop <= TOP_EPSILON_PX;
    if (!hasReachedTop) break;

    activeId = id;
  }

  return activeId;
};

export const useScrollSpy = (
  sectionIds: string[],
  containerRef: React.RefObject<HTMLElement | null>
): { activeId: string | null } => {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

  const updateActiveId = useEffectEvent((container: HTMLElement) => {
    if (sectionIds.length === 0) return;
    setActiveId(getActiveSectionId(sectionIds, container));
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return () => {};

    const handleUpdate = () => updateActiveId(container);

    handleUpdate();
    container.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate);

    return () => {
      container.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [sectionIds, containerRef]);

  return { activeId };
};
